/**
 * Invoice Template Context
 *
 * Provides shared invoice template state backed by the API service layer.
 * The API is the sole source of truth — no localStorage caching.
 * This allows templates edited in Settings > Templates to persist across
 * page navigations and be available to the Invoice Generation Wizard.
 */

import React, { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import type { InvoiceTemplate } from '@/lib/invoice-template-types';
import {
  getInvoiceTemplates as fetchTemplatesFromApi,
  createInvoiceTemplate as createTemplateApi,
  updateInvoiceTemplate as updateTemplateApi,
  deleteInvoiceTemplate as deleteTemplateApi,
} from '@/services/invoiceTemplateService';
import { useCompanyChange } from '@/hooks/use-company-change';

interface InvoiceTemplateContextType {
  templates: InvoiceTemplate[];
  reloadTemplates: () => Promise<InvoiceTemplate[]>;
  addTemplate: (template: InvoiceTemplate) => Promise<InvoiceTemplate>;
  updateTemplate: (id: number, updates: Partial<InvoiceTemplate>) => Promise<void>;
  updateTemplateLocal: (id: number, updates: Partial<InvoiceTemplate>) => void;
  deleteTemplate: (id: number) => Promise<void>;
  setDefault: (id: number) => Promise<void>;
  getDefaultTemplate: () => InvoiceTemplate | undefined;
  getTemplate: (id: number) => InvoiceTemplate | undefined;
  setTemplates: React.Dispatch<React.SetStateAction<InvoiceTemplate[]>>;
}

const InvoiceTemplateContext = createContext<InvoiceTemplateContextType | undefined>(undefined);

export function InvoiceTemplateProvider({ children }: { children: ReactNode }) {
  const [templates, setTemplates] = useState<InvoiceTemplate[]>([]);

  // Re-fetch the shared, company-scoped templates from the API (source of truth).
  const reloadTemplates = useCallback(async () => {
    const apiTemplates = await fetchTemplatesFromApi();
    setTemplates(apiTemplates);
    return apiTemplates;
  }, []);

  const loadTemplates = useCallback(() => {
    let cancelled = false;
    fetchTemplatesFromApi()
      .then((apiTemplates) => {
        if (!cancelled) {
          setTemplates(apiTemplates);
        }
      })
      .catch((err) => {
        console.error('Failed to load invoice templates from API:', err);
      });
    return () => { cancelled = true; };
  }, []);

  // Load templates from API on mount
  useEffect(() => {
    return loadTemplates();
  }, [loadTemplates]);

  // Re-fetch templates when the active company changes
  useCompanyChange(() => {
    loadTemplates();
  });

  // Local-only merge used for live builder preview. Does NOT hit the API — the
  // server write happens once, on explicit Save (see updateTemplate). This
  // prevents the previous "write on every keystroke / on open" storm where
  // unordered, fire-and-forget PUTs raced and clobbered each other.
  const updateTemplateLocal = useCallback((id: number, updates: Partial<InvoiceTemplate>) => {
    setTemplates(prev => prev.map(template =>
      template.id === id ? { ...template, ...updates } : template
    ));
  }, []);

  const addTemplate = useCallback(async (template: InvoiceTemplate): Promise<InvoiceTemplate> => {
    try {
      const created = await createTemplateApi(template);
      // Reload so local state exactly mirrors the persisted, server-assigned row.
      await reloadTemplates().catch(() => {
        setTemplates(prev => [...prev, created]);
      });
      return created;
    } catch (err) {
      // Do not leave a phantom local template that was never persisted.
      await reloadTemplates().catch(() => {});
      throw err;
    }
  }, [reloadTemplates]);

  const updateTemplate = useCallback(async (id: number, updates: Partial<InvoiceTemplate>): Promise<void> => {
    let merged: InvoiceTemplate | undefined;
    setTemplates(prev => {
      const updated = prev.map(template =>
        template.id === id
          ? { ...template, ...updates, updatedAt: new Date() }
          : template
      );
      merged = updated.find(t => t.id === id);
      return updated;
    });
    if (!merged) return;
    try {
      await updateTemplateApi(merged);
    } catch (err) {
      // Revert optimistic local state to the server's source of truth so the
      // UI never shows a change that was not actually persisted.
      await reloadTemplates().catch(() => {});
      throw err;
    }
  }, [reloadTemplates]);

  const deleteTemplate = useCallback(async (id: number): Promise<void> => {
    setTemplates(prev => prev.filter(template => template.id !== id));
    try {
      await deleteTemplateApi(id);
    } catch (err) {
      await reloadTemplates().catch(() => {});
      throw err;
    }
  }, [reloadTemplates]);

  const setDefault = useCallback(async (id: number): Promise<void> => {
    let defaultTemplate: InvoiceTemplate | undefined;
    setTemplates(prev => {
      const updated = prev.map(template => ({
        ...template,
        isDefault: template.id === id,
      }));
      defaultTemplate = updated.find(t => t.id === id);
      return updated;
    });
    if (!defaultTemplate) return;
    try {
      await updateTemplateApi(defaultTemplate);
    } catch (err) {
      await reloadTemplates().catch(() => {});
      throw err;
    }
  }, [reloadTemplates]);

  const getDefaultTemplate = useCallback(() => {
    return templates.find(t => t.isDefault) || templates[0];
  }, [templates]);

  const getTemplate = useCallback((id: number) => {
    return templates.find(template => template.id === id);
  }, [templates]);

  return (
    <InvoiceTemplateContext.Provider
      value={{
        templates,
        reloadTemplates,
        addTemplate,
        updateTemplate,
        updateTemplateLocal,
        deleteTemplate,
        setDefault,
        getDefaultTemplate,
        getTemplate,
        setTemplates,
      }}
    >
      {children}
    </InvoiceTemplateContext.Provider>
  );
}

export function useInvoiceTemplates() {
  const context = useContext(InvoiceTemplateContext);
  if (context === undefined) {
    throw new Error('useInvoiceTemplates must be used within an InvoiceTemplateProvider');
  }
  return context;
}
